import { CardsContainer } from "./table-container/CardsContainer";
import { ShowAdversariesLayer } from "./ShowAdversariesLayer";
import { GameOverLayer } from "./GameOverLayer";
import { WinningHandsLayer } from "./WinningHandsLayer";
import { AudioManager } from "./../../managers/AudioManager";
import { GameVars } from "../../GameVars";
import { GameConstants } from "../../GameConstants";
import { RoomManager } from "../../managers/RoomManager";
import { QuitLayer } from "./QuitLayer";
import { GUI } from "./gui/GUI";
import { TableContainer } from "./table-container/TableContainer";
import { PlayersContainer } from "./players-container/PlayersContainer";
import { Background } from "../background/Background";
import { Table } from "./Table";
import { HUD } from "./hud/HUD";
import { BananaLayer } from "./banana/BananaLayer";
import { WinnerPlayerLayer } from "./WinnerPlayerLayer";
import { WinnerLayer } from "./WinnerLayer";
import { InfosLayer } from "./InfosLayer";

export class RoomScene extends Phaser.Scene {

    public static currentInstance: RoomScene;

    private tableContainer: TableContainer;
    private playersContainer: PlayersContainer;
    private gui: GUI;
    private hud: HUD;
    private cardsContainer: CardsContainer;
    private quitLayer: QuitLayer;
    private gameOverLayer: GameOverLayer;
    private winnerLayer: WinnerLayer;
    private winningHandsLayer: WinningHandsLayer;
    private winnerPlayerLayer: WinnerPlayerLayer;
    private bananaLayer: BananaLayer;
    private infosLayer: InfosLayer;

    constructor() {

        super("RoomScene");

        RoomScene.currentInstance = this;
    }

    public create(): void {

        GameVars.currentScene = this;

        RoomManager.init(this.game);

        this.createAnimations();

        let ids = [GameVars.playerId].concat(RoomManager.getIds(GameConstants.ADVERSARYS_LENGTH));

        let isPlayerQueued = RoomManager.IsPlayerQueued;
        if (RoomManager.IsPlayerQueued) {
            ids = [GameVars.playerId].concat(GameVars.gameState.players.map(p => p.id));
        }

        RoomManager.initPokerState(ids, GameConstants.ROOM_PRICE, GameConstants.BIG_BLINDS[0]);

        const background = new Background(this);
        this.add.existing(background);

        if (!this.sys.game.device.os.desktop) {
            const hideInfoBackground = new Phaser.GameObjects.Graphics(this);
            hideInfoBackground.fillStyle(0x000000, .01);
            hideInfoBackground.fillRect(0, 0, GameConstants.GAME_WIDTH, GameConstants.GAME_HEIGHT);
            hideInfoBackground.setInteractive(new Phaser.Geom.Rectangle(0, 0, GameConstants.GAME_WIDTH, GameConstants.GAME_HEIGHT), Phaser.Geom.Rectangle.Contains);
            hideInfoBackground.on("pointerdown", () => {
                RoomScene.currentInstance.hideInfo();
            });
            this.add.existing(hideInfoBackground);
        }

        const table = new Table(this);
        this.add.existing(table);

        this.tableContainer = new TableContainer(this);
        this.add.existing(this.tableContainer);

        this.playersContainer = new PlayersContainer(this, ids);
        this.add.existing(this.playersContainer);

        this.cardsContainer = new CardsContainer(this);
        this.add.existing(this.cardsContainer);

        this.gui = new GUI(this);
        this.add.existing(this.gui);

        this.hud = new HUD(this);
        this.add.existing(this.hud);
        this.hud.visible = false;

        this.winnerPlayerLayer = new WinnerPlayerLayer(this);
        this.add.existing(this.winnerPlayerLayer);

        this.bananaLayer = new BananaLayer(this);
        this.add.existing(this.bananaLayer);

        this.infosLayer = new InfosLayer(this, ids);
        this.add.existing(this.infosLayer);

        if (GameConstants.AI_MODE) {
            GameVars.gameState.players.shift();
        }
        if (!isPlayerQueued) {
            let showAdversariesLayer = new ShowAdversariesLayer(this, ids);
            this.add.existing(showAdversariesLayer);
        } else {
            this.startFirstHand();
        }

        AudioManager.playMusic("soundtrack_in-game", .5);
    }

    public startFirstHand(): void {

        this.playersContainer.showPlayers();
        RoomManager.startNewHand(true);
        this.hud.visible = true;
    }

    public updateInfo(): void {

        this.hud.updateInfo();
    }

    public showInfo(id: string): void {

        this.infosLayer.showInfo(id);
    }

    public showInfoMobile(id: string): void {

        this.infosLayer.showInfoMobile(id);
    }

    public hideInfo(): void {

        this.infosLayer.hideInfo();
    }

    public showAllinBackground(): void {

        this.playersContainer.showAllinPlayers();
    }

    public hideAllinBackground(): void {

        this.playersContainer.hiedAllinPlayers();
    }

    public playerTurn(): void {

        this.markPlayer();
        this.gui.activate();
    }

    public updatePot(): void {

        this.hud.updatePot();
    }

    public setValueButtons(): void {

        this.gui.setValues();
    }

    public deactivateButtons(): void {

        this.gui.deactivate();
    }

    public showQuitLayer(): void {

        this.quitLayer = new QuitLayer(this);
        this.add.existing(this.quitLayer);
    }

    public removeQuitLayer(): void {

        this.quitLayer.destroy();
    }

    public showGameOverLayer(): void {

        if (this.gameOverLayer) {
            return;
        }

        this.gameOverLayer = new GameOverLayer(this);
        this.add.existing(this.gameOverLayer);
    }

    public removeGameOverLayer(): void {

        this.gameOverLayer.destroy();
    }

    public showWinnerLayer(): void {

        this.winnerLayer = new WinnerLayer(this);
        this.add.existing(this.winnerLayer);
    }

    public removeWinnerLayer(): void {

        this.winnerLayer.destroy();
    }

    public showWinningHandsLayer(): void {

        if (!this.sys.game.device.os.desktop && this.winningHandsLayer) {
            this.removeWinningHandsLayer();
        } else {
            this.winningHandsLayer = new WinningHandsLayer(this);
            this.add.existing(this.winningHandsLayer);
        }
    }

    public removeWinningHandsLayer(): void {

        this.winningHandsLayer.destroy();
        this.winningHandsLayer = null;
    }

    public showBananaPopcorns(): void {

        this.bananaLayer.showBananaPopcorns();
    }

    public showBananaRise(): void {

        this.bananaLayer.showBananaRise();
    }

    public hideBananaPopcorns(): void {

        this.bananaLayer.hideBananaPopcorns();
    }

    public markPlayer(): void {

        this.playersContainer.markPlayer();
    }

    public unmarkPlayers(): void {

        this.playersContainer.unmarkPlayers();
    }

    public startNewHand(first?: boolean): void {
        // por si deja de sonar el loop por cualquier razon
        AudioManager.playMusic("soundtrack_in-game", .5);
        AudioManager.volumeMusic(.5);

        // eliminar jugadores

        for (let i = 0; i < GameVars.gameState.players.length; i++) {
            if (GameVars.gameState.players[i] && GameVars.gameState.players[i].chips <= 0) {
                GameVars.gameState.players.splice(i, 1);
                i--;
            }
        }

        if (!RoomManager.getPlayer(GameVars.playerId) && GameVars.gameState.players.length !== 1) {
            this.time.addEvent({
                delay: 3000 / GameVars.gameSpeed, callback: () => {
                    if (!GameConstants.AI_MODE) {
                        // this.showGameOverLayer();
                    }
                }, callbackScope: this
            });
        }

        this.playersContainer.removeEliminatedPlayers();

        RoomManager.resetCommunity();
        this.resetTable();

        if (!first) {
            this.playersContainer.finalizeActions();
        }

        if (GameVars.gameState.players.length === 1) {
            this.time.addEvent({
                delay: 3000 / GameVars.gameSpeed, callback: () => {
                    RoomManager.setWinner();
                }, callbackScope: this
            });
            return;
        }

        this.time.addEvent({
            delay: 1000 / GameVars.gameSpeed, callback: () => {
                RoomManager.rejoinPlayerIfQueued();

                RoomManager.setPlayersNewHand();
                this.playersContainer.updateAllChips();
                // this.playersContainer.setDealer();
                this.tableContainer.setDealer(this.playersContainer.getDealer());

                RoomManager.newCards();
                RoomManager.dealPlayersCards();
                this.playersContainer.dealPlayersCards();

            }, callbackScope: this
        });

        this.time.addEvent({
            delay: 3000 / GameVars.gameSpeed, callback: () => {
                RoomManager.preflop();
            }, callbackScope: this
        });
    }

    public resetTable(): void {

        this.playersContainer.resetTable();
        this.tableContainer.resetTable();
        this.cardsContainer.resetTable();
    }

    public updateBets(id: string, type?: string): void {

        this.playersContainer.updateBets(id, type);
    }

    public hideCurrentPlayerCards(): void {

        this.playersContainer.hideCurrentPlayerCards();
    }

    public getBet(id: string): void {

        this.markPlayer();

        let values = this.playersContainer.getBet(id);

        RoomManager.updateActions(values.bet, id);

        let time = Math.random() * Math.max(0, values.handScore) * 10;

        this.time.addEvent({
            delay: (500 + time) / GameVars.gameSpeed, callback: () => {

                if (values.bluff) {
                    this.playersContainer.bluffAnimation(id);
                } else {
                    this.playersContainer.decisionAnimation(id);
                }

            }, callbackScope: this
        });

        this.time.addEvent({
            delay: (1500 + time) / GameVars.gameSpeed, callback: () => {
                RoomManager.playerBet(values.bet);
            }, callbackScope: this
        });
    }

    public showLeave(): void {

        this.gui.showLeave();
    }

    public resetBets(): void {

        this.playersContainer.resetBets();
        this.tableContainer.updatePot();
    }

    public addWageredToPot(): void {

        this.playersContainer.addWageredToPot();
    }

    public inactivePlayer(id: string): void {

        // this.unmarkPlayers();
        this.playersContainer.inactivePlayer(id);
    }

    public updateCommunityCards(): void {

        if (GameVars.gameState.state === GameConstants.FLOP) {
            this.cardsContainer.dealCard(GameVars.gameState.community[0], 0);
            this.cardsContainer.dealCard(GameVars.gameState.community[1], 300);
            this.cardsContainer.dealCard(GameVars.gameState.community[2], 600);
        } else if (GameVars.gameState.state === GameConstants.TURN) {
            this.cardsContainer.dealCard(GameVars.gameState.community[3], 0);
        } else if (GameVars.gameState.state === GameConstants.RIVER) {
            this.cardsContainer.dealCard(GameVars.gameState.community[4], 0);
        }

        this.playersContainer.updatePlayerHand();
    }

    public showCards(lastPlayer?: boolean): void {

        this.playersContainer.showCards(lastPlayer);
    }

    public decideShowCards(): void {

        this.playersContainer.decideShowCards();
    }

    public showCardsButtons(): void {

        this.gui.showCardsButtons();
    }

    public showBluffBanana(): void {

        let cards = GameVars.gameState.community.concat(RoomManager.getPlayer(GameVars.playerId).cards);
        let hand = Hand.solve(cards).name;

        if (hand === "High Card") {
            this.bananaLayer.showBananaBluff();
        }
    }

    public showWinners(winnersIds: string[], cards?: any[], handNames?: string[]): void {

        if (!GameConstants.AI_MODE) {
            RoomManager.changeSpeed(GameConstants.INITIAL_SPEED);
            this.gui.hideSkip();
            this.bananaLayer.hideBananaPopcorns();
        }

        let idPlayers = RoomManager.playersActive();

        RoomScene.currentInstance.resetBets();

        this.time.addEvent({
            delay: 1000 / GameVars.gameSpeed, callback: () => {

                if (idPlayers.length > 1) {
                    RoomScene.currentInstance.showCards();
                    this.time.addEvent({
                        delay: 1000, callback: () => {
                            this.playersContainer.showLosers(winnersIds);
                        }, callbackScope: this
                    });
                }
            }, callbackScope: this
        });

        let extra = GameVars.allin ? 0 : (RoomManager.playersActive().length + 1) * 100;

        this.time.addEvent({
            delay: (2500 + extra) / GameVars.gameSpeed, callback: () => {

                if (cards) {
                    this.updateWinnerHand(cards, winnersIds, handNames);
                }
            }, callbackScope: this
        });

        this.time.addEvent({
            delay: (2500 + extra + (cards ? 1000 : 0)) / GameVars.gameSpeed, callback: () => {

                this.playersContainer.showWinners(winnersIds);
                this.playersContainer.potToPlayer(winnersIds);
                this.tableContainer.hidePot();

                if (cards) {
                    this.addCardsFrames(cards, winnersIds, handNames);
                }

                if (idPlayers.length > 1 || idPlayers[0] !== GameVars.playerId) {
                    this.time.addEvent({
                        delay: 1000 / GameVars.gameSpeed, callback: () => {
                            RoomScene.currentInstance.afterShowWinners();
                        }, callbackScope: this
                    });
                }
            }, callbackScope: this
        });

        this.time.addEvent({
            delay: (3500 + (cards ? 1000 : 0)) / GameVars.gameSpeed, callback: () => {
                if (idPlayers.length === 1) {
                    if (idPlayers[0] === GameVars.playerId) {
                        RoomManager.showCardsButtons();
                    } else {
                        RoomScene.currentInstance.decideShowCards();
                    }
                }
            }, callbackScope: this
        });
    }

    public addCardsFrames(cards: any[], winnersIds: string[], handNames: string[]): void {

        this.cardsContainer.addCardsFrames(cards[0]);
        this.playersContainer.addCardsFrames(cards, winnersIds);
    }

    public updateWinnerHand(cards: any[], winnersIds: string[], handNames: string[]): void {

        this.winnerPlayerLayer.updateWinnerHand(handNames, winnersIds, cards);
        AudioManager.playSound(handNames[0]);

        if (GameVars.gameOver) {
            RoomScene.currentInstance.gui.hideLeave();
        }
    }

    public afterShowWinners(): void {

        this.time.addEvent({
            delay: 2000 / GameVars.gameSpeed, callback: () => {
                RoomScene.currentInstance.startNewHand();
                RoomScene.currentInstance.winnerPlayerLayer.removeWinnerHand();
            }, callbackScope: this
        });
    }

    public getCredibility(id: string): any {

        return this.playersContainer.getCredibility(id);
    }

    public setPlayerRivalry(id: string, winners: string[]) {

        this.playersContainer.setPlayerRivalry(id, winners);
    }

    private createAnimations(): void {

        this.anims.create({ key: "confetti", frames: this.anims.generateFrameNames("texture_atlas_1", { prefix: "confetti_", start: 1, end: 12, zeroPad: 2, suffix: ".png" }), frameRate: 16 });
        this.anims.create({ key: "fire_raise", frames: this.anims.generateFrameNames("texture_atlas_1", { prefix: "fire_raise_", start: 0, end: 20, zeroPad: 2, suffix: ".png" }), frameRate: 24 });
        this.anims.create({ key: "winner_streak", frames: this.anims.generateFrameNames("texture_atlas_1", { prefix: "winner_streak_", start: 24, end: 47, zeroPad: 2, suffix: ".png" }), frameRate: 60, repeat: -1 });
        this.anims.create({ key: "sparks", frames: this.anims.generateFrameNames("texture_atlas_1", { prefix: "sparks_", start: 1, end: 43, zeroPad: 2, suffix: ".png" }), frameRate: 30 });
    }
}
